import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, finalize, Observable, switchMap, tap, throwError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { IBCAuthService } from '../../features/service/auth.service';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { CorrelationIdService } from './telemetry';
import { environment } from 'src/environments/environment';
import { isCustomsCityProcessClearanceBusinessHttpError } from '../utility/customs-city-http.util';

/**
 * HTTP Interceptor that:
 * 1. Adds X-Correlation-ID header to internal API requests only
 * 2. Handles 401 errors with token refresh
 *
 * Note: API errors are NOT logged here - they are handled by the backend
 */
@Injectable({
  providedIn: 'root',
})
export class IntercepterService implements HttpInterceptor {
  private refreshTokenRequest?: Observable<any>;
  private correlationIdService: CorrelationIdService | null = null;

  constructor(
    private readonly errorHandler: ErrorHandlerService,
    private readonly spinnerService: SpinnerService,
    private readonly authService: IBCAuthService,
    private readonly injector: Injector
  ) {}

  /**
   * Lazy initialization of correlation ID service to avoid circular dependencies
   */
  private initCorrelationIdService(): void {
    this.correlationIdService ??= this.injector.get(CorrelationIdService);
  }

  /**
   * Check if the request is to the telemetry endpoint (to avoid adding headers)
   */
  private isTelemetryRequest(url: string): boolean {
    return url.includes('/v1/telemetry/');
  }

  /** FR-012: permissions load failures are handled in UserEffectivePermissionsService (F-008). */
  private isClientHandledPermissionsRequest(url: string): boolean {
    return url.includes('/loadEffectivePermissions');
  }

  /**
   * Check if the request is to our internal API (not external services like ipify.org)
   */
  private isInternalApiRequest(url: string): boolean {
    const apiBaseUrl = environment.API_BASE_URL;

    // Check if URL starts with our API base URL or is a relative URL
    return (
      url.startsWith(apiBaseUrl) ||
      url.startsWith('/') || // Relative URLs are internal
      !url.startsWith('http') // Non-http URLs are internal
    );
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.initCorrelationIdService();

    // Only add correlation headers to internal API requests (not external services)
    const shouldAddHeaders =
      this.isInternalApiRequest(request.url) &&
      !this.isTelemetryRequest(request.url);

    // Clone request with correlation ID header for internal APIs only
    let modifiedRequest = request;
    if (shouldAddHeaders && this.correlationIdService) {
      const correlationId = this.correlationIdService.getCorrelationId();
      const messageId = this.correlationIdService.generateMessageId();

      modifiedRequest = request.clone({
        setHeaders: {
          'X-Correlation-ID': correlationId,
          'X-Message-ID': messageId,
        },
      });
    }

    // Increment the pending-request counter once per intercepted request.
    // The symmetric decrement lives in the finalize() below, which fires on
    // completion, error, AND cancellation — keeping the count correct even
    // when a component is destroyed mid-flight.
    this.spinnerService.showSpinner();

    return next.handle(modifiedRequest).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(modifiedRequest, next);
        }

        return throwError(() => error);
      }),
      tap({
        error: _error => {
          if (_error instanceof HttpErrorResponse) {
            // Generic toast only for server/connection failures, or client
            // errors with no business `code` (coded errors are shown by the page).
            // TODO: give every coded 4xx a page-level message, then drop the no-code branch.
            const showToast =
              _error.status >= 500 ||
              _error.status === 0 ||
              (_error.status !== 401 &&
                !_error.error?.code &&
                !this.isTelemetryRequest(modifiedRequest.url) &&
                !this.isClientHandledPermissionsRequest(modifiedRequest.url) &&
                !isCustomsCityProcessClearanceBusinessHttpError(_error));
            if (showToast) {
              this.errorHandler.handleError(_error);
            }
          }
        },
      }),
      // Single hide point — covers success, error, and request cancellation.
      // SpinnerService.hideSpinner() is now reference-counted so the spinner
      // stays visible until every concurrent request has resolved.
      finalize(() => this.spinnerService.hideSpinner())
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    this.refreshTokenRequest ??= this.authService.refreshSession().pipe(
      finalize(() => {
        this.refreshTokenRequest = undefined;
      })
    );

    return this.refreshTokenRequest.pipe(
      switchMap(() => {
        return next.handle(
          request.clone({
            setHeaders: {
              authorization: this.authService.getJWTToken() ?? '',
            },
          })
        );
      }),
      catchError(error => {
        if (
          error.status === 403 ||
          error.status === 401 ||
          error instanceof InteractionRequiredAuthError
        ) {
          this.authService.showWarningPopupAndLogout();
        }

        return throwError(() => error);
      })
    );
  }
}
